import { useCallback, useMemo } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  changeTimeRule,
  checkTimeRuleSaveDisabled,
  deleteTimeRule,
  discardChanges,
  firstTimeRuleSelector,
  saveTimeRule,
  secondTimeRuleSelector,
  setFirstTimeRule,
  setSecondTimeRule,
  timeRuleMapSelector,
} from 'slices/timeRule.slice';
import type { TTimeRule } from '@timeedit/types';
import { hasPermissions } from '@timeedit/types/lib/utils/permissionCalculator';
import { TEDrawer } from '@timeedit/ui-components';
import TimeRule from './TimeRule';

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const TimeRuleDrawer = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  const firstTimeRule = useAppSelector(firstTimeRuleSelector);
  const secondTimeRule = useAppSelector(secondTimeRuleSelector);
  const timeRuleMap: Record<number | string, TTimeRule> =
    useAppSelector(timeRuleMapSelector);

  const setFirstResource = useCallback(
    (timeRule: TTimeRule) => dispatch(setFirstTimeRule(timeRule)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (timeRule: TTimeRule) => dispatch(setSecondTimeRule(timeRule)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    timeRuleMap,
    firstTimeRule,
    secondTimeRule,
    setFirstResource,
    setSecondResource
  );

  const firstTimeRuleSaveDisabled = useMemo(
    () => dispatch(checkTimeRuleSaveDisabled(true)),
    [firstTimeRule]
  );

  const secondTimeRuleSaveDisabled = useMemo(
    () => dispatch(checkTimeRuleSaveDisabled(false)),
    [secondTimeRule]
  );

  const onDelete = useCallback((timeRule: TTimeRule) => {
    dispatch(deleteTimeRule(timeRule.id));
  }, []);

  const onSave = useCallback((timeRule: TTimeRule) => {
    dispatch(saveTimeRule(timeRule));
  }, []);

  const onClose = useCallback(
    (isFirstDrawer: boolean) =>
      confirmDiscardPaddingChanges(isFirstDrawer, () => {
        if (isFirstDrawer) {
          dispatch(setFirstTimeRule(undefined));
        } else {
          dispatch(setSecondTimeRule(undefined));
        }
      }),
    []
  );

  const userIsSuper = useMemo(
    () =>
      user &&
      hasPermissions(user?.appPermissions, user?.scopes, {
        scopes: ['TE_ADMIN::super'],
      }),
    [user]
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: firstTimeRule?.name || '',
        content: (
          <TimeRule
            timeRule={firstTimeRule}
            userIsSuper={userIsSuper}
            onChange={({ key, value, object }) =>
              dispatch(
                changeTimeRule({ isFirstDrawer: true, key, value, object })
              )
            }
          />
        ),
        drawerOpen: !!firstTimeRule,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('time_rule_confirmation_delete')}
            handleDelete={() => onDelete(firstTimeRule!)}
            isDeleteDisable={!userIsSuper}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: true,
                  id: firstTimeRule!.id,
                })
              )
            }
            handleSave={() => onSave(firstTimeRule!)}
            isSaveDisable={firstTimeRuleSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(true),
      }}
      secondDrawer={{
        title: secondTimeRule?.name || '',
        content: (
          <TimeRule
            userIsSuper={userIsSuper}
            timeRule={secondTimeRule}
            onChange={({ key, value, object }) =>
              dispatch(
                changeTimeRule({ isFirstDrawer: false, key, value, object })
              )
            }
          />
        ),
        drawerOpen: !!secondTimeRule,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('time_rule_confirmation_delete')}
            handleDelete={() => onDelete(secondTimeRule!)}
            isDeleteDisable={!userIsSuper}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: false,
                  id: secondTimeRule!.id,
                })
              )
            }
            handleSave={() => onSave(secondTimeRule!)}
            isSaveDisable={secondTimeRuleSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(false),
      }}
    />
  );
};

export default TimeRuleDrawer;
